import React from "react";
import { Field } from "common/components/Field";
import { commaSeparatedEmail, phoneNumber } from "common/helpers/FormValidate";
import { P } from "common/components/Paragraph";
import { ToggleControl } from "common/components/Toggle";
import { useAppDispatch, useAppSelector } from "common/hooks/storeHooks";
import { UserActions } from "common/reducers/user.actions";
import { currentUser, notificationPrefSelected, userPreferences } from "common/selectors/user.selectors";
import { List } from "immutable";
import { NotificationControls } from "user/Profile/Notifications/NotificationControls";
import { Accordion } from "common/components/Accordion";

export const notificationControls = List([
  { label: "Customer Photo Uploaded", key: "customer_photo_uploaded" },
  { label: "Signable Documents", key: "signable_documents_signed" },
  { label: "Signed Document (Agreement)", key: "signed_document_signed" },
  { label: "Job Created", key: "job_created" },
  { label: "Job Reassigned", key: "job_reassigned" },
  { label: "Payment Receipts", key: "payment_receipt" },
  { label: "Payment Refunds", key: "payment_refund" },
]);

export const Notifications: React.FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(currentUser);
  const path = ["preferences", "config", "notifications"];

  const notificationsEmailConfigSelected = useAppSelector((state) =>
    notificationPrefSelected(state, { userId: user?.id, path: ["config", "notifications", "via_email"] }),
  );

  const notificationsSmsConfigSelected = useAppSelector((state) =>
    notificationPrefSelected(state, { userId: user?.id, path: ["config", "notifications", "via_sms"] }),
  );

  const handleToggleAll = (event: any) => {
    const { checked, name } = event.target;
    if (checked) {
      dispatch(UserActions.update({ path: path, name: name, value: notificationControls.map((n) => n.key) }));
    } else {
      dispatch(UserActions.update({ path: path, name: name, value: List() }));
    }
  };

  return (
    <div className="mt-3">
      <P weight="bold" className="text-secondary-500 mb-5" size="xs">
        You will receive notifications at the email address provided by your account administrator. You can change your
        preferences at any time by following the instructions provided below.
      </P>
      <div className="flex flex-col gap-xl">
        <Accordion
          title="Email Notifications"
          description="Manage your email notifications settings"
          rightComponent={
            <ToggleControl
              value={notificationsEmailConfigSelected}
              handleInput={handleToggleAll}
              label="Select All"
              name="via_email"
              size="md"
            />
          }>
          <div className="flex flex-col gap-lg">
            <div className="grid grid-cols-4">
              <div className="col-span-1">
                <Field
                  type="inputText"
                  validate={commaSeparatedEmail}
                  name="preferences.config.notifications.email"
                  label="Email"
                />
              </div>
            </div>
            <NotificationControls key="email" control_type="via_email" controls={notificationControls} />
            <div className="grid grid-cols-5">
              <div className=" flex col-span-4 gap-lg items-start">
                <span className={"material-symbols-outlined font-semibold ml-xs text-[16px] text-secondary-700"}>
                  info
                </span>
                <P weight="regular" className="text-secondary-500 mb-5" size="xs">
                  To update the email address where you receive notifications or to resubscribe after opting-out, enter
                  your email address, check the notifications by email box and click Save Changes. To stop receiving
                  notifications by email, uncheck the notifications box above and click Save Changes or click the
                  unsubscribe link in any notification email.
                </P>
              </div>
            </div>
          </div>
        </Accordion>
        <Accordion
          title="Number Notifications"
          description="Manage your number notifications settings"
          rightComponent={
            <ToggleControl
              value={notificationsSmsConfigSelected}
              handleInput={handleToggleAll}
              label="Select All"
              name="via_sms"
              size="md"
            />
          }>
          <div className="flex flex-col gap-lg">
            <div className="grid grid-cols-4">
              <div className="col-span-1">
                <Field type="inputText" validate={phoneNumber} name="preferences.sms_number.number" label="Number" />
              </div>
            </div>
            <NotificationControls key="sms" control_type="via_sms" controls={notificationControls} />
            <div className="flex gap-lg items-start">
              <span className={"material-symbols-outlined font-semibold ml-xs text-[16px] text-secondary-700"}>
                info
              </span>
              <div>
                <P weight="regular" className="text-secondary-500" size="xs">
                  By entering a mobile number, checking the notifications by text box and clicking Save Changes, you
                  consent to receiving notifications via automated text messages to the mobile phone number you provided
                  above. Your consent to these communications is not required for any purchase. Message frequency for
                  notifications varies. Message and data rates may apply. Reply “STOP” to a notification text or uncheck
                  the notifications box above and click Save Changes to end notification texts.
                </P>
                <P weight="regular" className="text-secondary-500" size="xs">
                  If you opt-out of receiving notification texts by replying “STOP” to a notification text message and
                  you would like to resubscribe, reply “UNSTOP”.
                </P>
                <P weight="regular" className="text-secondary-500 mb-5" size="xs">
                  If you opt-out of receiving notification texts by unchecking the notifications box above and you would
                  like to subscribe again, enter your mobile number, check the notifications box and click Save Changes.
                </P>
              </div>
            </div>
          </div>
        </Accordion>
      </div>
    </div>
  );
};
